/** Activity Log 動作類型（與後端 action 對齊） */
export const ACTIVITY_ACTIONS = {
  PUBLISH: 'publish',
  TEMPLATE_CREATE: 'template_create',
  TEMPLATE_UPDATE: 'template_update',
  TEMPLATE_DELETE: 'template_delete',
  PLATFORM_CREATE: 'platform_create',
  PLATFORM_UPDATE: 'platform_update',
  PLATFORM_DELETE: 'platform_delete',
  CLIENT_CREATE: 'client_create',
  CLIENT_UPDATE: 'client_update',
  CLIENT_DELETE: 'client_delete',
  GUIDE_CREATE: 'guide_create',
  GUIDE_UPDATE: 'guide_update',
  GUIDE_DELETE: 'guide_delete',
  /** @deprecated 舊資料相容；新寫入請用 GUIDE_CREATE / UPDATE / DELETE */
  GUIDE_SAVE: 'guide_save',
  DOC_CREATE: 'doc_create',
  DOC_UPDATE: 'doc_update',
  DOC_DELETE: 'doc_delete',
  /** 複製子本（更動細節歸類 [新增]） */
  DOC_DUPLICATE: 'doc_duplicate',
  SECTION_CREATE: 'section_create',
  SECTION_DELETE: 'section_delete',
  SECTION_DUPLICATE: 'section_duplicate',
  SECTION_REORDER: 'section_reorder',
  SECTION_MOVE: 'section_move',
}

/**
 * 列表「操作」分組（篩選／顯示用）
 * Template Management / Platform Management / User Guide / Section / 子本 / Publish
 */
export const ACTIVITY_OPERATION_GROUPS = {
  TEMPLATE_MANAGEMENT: {
    key: 'template_management',
    label: 'Template Management',
    color: 'blue',
    actions: [
      ACTIVITY_ACTIONS.TEMPLATE_CREATE,
      ACTIVITY_ACTIONS.TEMPLATE_UPDATE,
      ACTIVITY_ACTIONS.TEMPLATE_DELETE,
    ],
  },
  PLATFORM_MANAGEMENT: {
    key: 'platform_management',
    label: 'Platform Management',
    color: 'cyan',
    actions: [
      ACTIVITY_ACTIONS.PLATFORM_CREATE,
      ACTIVITY_ACTIONS.PLATFORM_UPDATE,
      ACTIVITY_ACTIONS.PLATFORM_DELETE,
      ACTIVITY_ACTIONS.CLIENT_CREATE,
      ACTIVITY_ACTIONS.CLIENT_UPDATE,
      ACTIVITY_ACTIONS.CLIENT_DELETE,
    ],
  },
  USER_GUIDE: {
    key: 'user_guide',
    label: 'User Guide',
    color: 'purple',
    actions: [
      ACTIVITY_ACTIONS.GUIDE_CREATE,
      ACTIVITY_ACTIONS.GUIDE_UPDATE,
      ACTIVITY_ACTIONS.GUIDE_DELETE,
      ACTIVITY_ACTIONS.GUIDE_SAVE,
    ],
  },
  SECTION: {
    key: 'section',
    label: 'Section',
    color: 'orange',
    actions: [
      ACTIVITY_ACTIONS.SECTION_CREATE,
      ACTIVITY_ACTIONS.SECTION_DELETE,
      ACTIVITY_ACTIONS.SECTION_DUPLICATE,
      ACTIVITY_ACTIONS.SECTION_REORDER,
      ACTIVITY_ACTIONS.SECTION_MOVE,
    ],
  },
  DOC: {
    key: 'doc',
    label: '子本',
    color: 'geekblue',
    actions: [
      ACTIVITY_ACTIONS.DOC_CREATE,
      ACTIVITY_ACTIONS.DOC_DELETE,
      ACTIVITY_ACTIONS.DOC_UPDATE,
      ACTIVITY_ACTIONS.DOC_DUPLICATE,
    ],
  },
  PUBLISH: {
    key: 'publish',
    label: 'Publish',
    color: 'green',
    actions: [
      ACTIVITY_ACTIONS.PUBLISH,
    ],
  },
}

export const ACTIVITY_OPERATION_GROUP_LIST = Object.values(ACTIVITY_OPERATION_GROUPS)

export const getOperationGroupByAction = action => (
  ACTIVITY_OPERATION_GROUP_LIST.find(g => g.actions.includes(action))
  || null
)

export const getOperationGroupMeta = (action) => {
  const group = getOperationGroupByAction(action)
  if (group) return { label: group.label, color: group.color, key: group.key }
  return { label: action || '—', color: 'default', key: '' }
}

/** 更動細節開頭：[新增]／[修改]／[刪除]（複製 Section／子本 歸 [新增]） */
export const getChangeBracketLabel = (action) => {
  if (!action) return '修改'
  if (
    action.endsWith('_create')
    || action === ACTIVITY_ACTIONS.SECTION_DUPLICATE
    || action === ACTIVITY_ACTIONS.DOC_DUPLICATE
  ) return '新增'
  if (action.endsWith('_delete')) return '刪除'
  if (action === ACTIVITY_ACTIONS.SECTION_MOVE) return '移動'
  return '修改'
}

/** @deprecated 改用 getOperationGroupMeta；保留舊 label 供相容 */
export const ACTIVITY_ACTION_META = {
  [ACTIVITY_ACTIONS.PUBLISH]: { label: '發佈', color: 'green' },
  [ACTIVITY_ACTIONS.TEMPLATE_CREATE]: { label: '新增 Template', color: 'blue' },
  [ACTIVITY_ACTIONS.TEMPLATE_UPDATE]: { label: '修改 Template', color: 'blue' },
  [ACTIVITY_ACTIONS.TEMPLATE_DELETE]: { label: '刪除 Template', color: 'red' },
  [ACTIVITY_ACTIONS.PLATFORM_CREATE]: { label: '新增 Platform', color: 'blue' },
  [ACTIVITY_ACTIONS.PLATFORM_UPDATE]: { label: '修改 Platform', color: 'blue' },
  [ACTIVITY_ACTIONS.PLATFORM_DELETE]: { label: '刪除 Platform', color: 'red' },
  [ACTIVITY_ACTIONS.CLIENT_CREATE]: { label: '新增 Client', color: 'blue' },
  [ACTIVITY_ACTIONS.CLIENT_UPDATE]: { label: '修改 Client', color: 'blue' },
  [ACTIVITY_ACTIONS.CLIENT_DELETE]: { label: '刪除 Client', color: 'red' },
  [ACTIVITY_ACTIONS.GUIDE_CREATE]: { label: '新增 User Guide', color: 'purple' },
  [ACTIVITY_ACTIONS.GUIDE_UPDATE]: { label: '修改 User Guide', color: 'purple' },
  [ACTIVITY_ACTIONS.GUIDE_DELETE]: { label: '刪除 User Guide', color: 'red' },
  [ACTIVITY_ACTIONS.GUIDE_SAVE]: { label: '儲存 User Guide', color: 'purple' },
  [ACTIVITY_ACTIONS.DOC_CREATE]: { label: '新建子本', color: 'cyan' },
  [ACTIVITY_ACTIONS.DOC_UPDATE]: { label: '修改子本', color: 'cyan' },
  [ACTIVITY_ACTIONS.DOC_DELETE]: { label: '刪除子本', color: 'red' },
  [ACTIVITY_ACTIONS.DOC_DUPLICATE]: { label: '複製子本', color: 'cyan' },
  [ACTIVITY_ACTIONS.SECTION_CREATE]: { label: '新增 Section', color: 'orange' },
  [ACTIVITY_ACTIONS.SECTION_DELETE]: { label: '刪除 Section', color: 'red' },
  [ACTIVITY_ACTIONS.SECTION_DUPLICATE]: { label: '複製 Section', color: 'orange' },
  [ACTIVITY_ACTIONS.SECTION_REORDER]: { label: '拖曳排序', color: 'default' },
  [ACTIVITY_ACTIONS.SECTION_MOVE]: { label: '移動 Section', color: 'orange' },
}

/** 保留天數（半年） */
export const ACTIVITY_LOG_RETENTION_DAYS = 183
